import React, { useState, useEffect, useRef, useMemo, forwardRef } from "react";
import { Dropdown } from "react-bootstrap";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";

const EquipmentMultiSelectDropdown = ({
  ignoreScope,
  uniqueEquipmentWar,
  selectedEquipment,
  setSelectedEquipment,
  selectedProject,
  selectedGroup,
  setSelectedGroup,
  closeDropdown,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [equipmentGroups, setEquipmentGroups] = useState([]);

  const dropdownRef = useRef(null);
  const groupDropdownRef = useRef(null);

  // --- Normalizers ---
  const equipmentNumbers = useMemo(() => {
    // Convert array of objects/strings into array of strings (equipment numbers)
    if (!Array.isArray(uniqueEquipmentWar)) return [];
    return uniqueEquipmentWar
      .map((item) =>
        typeof item === "string"
          ? item
          : item?.equipment_number ?? item?.eqNo ?? item?.number ?? ""
      )
      .filter(Boolean);
  }, [uniqueEquipmentWar]);

  const groupNames = useMemo(() => {
    if (!Array.isArray(equipmentGroups)) return [];
    return equipmentGroups
      .map((g) =>
        typeof g === "string"
          ? g
          : g?.group_name ?? g?.equipment_name ?? g?.name ?? ""
      )
      .filter(Boolean);
  }, [equipmentGroups]);

  // Toggle dropdown with proper state management
  const toggleDropdown = (isOpen) => {
    if (ignoreScope === "equipment_name") {
      setGroupDropdownOpen(isOpen);
    } else {
      setDropdownOpen(isOpen);
    }
    if (!isOpen && closeDropdown) closeDropdown();
  };

  // Handle click outside for both dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        ignoreScope === "equipment_name" &&
        groupDropdownRef.current &&
        !groupDropdownRef.current.contains(event.target)
      ) {
        toggleDropdown(false);
      }
      if (
        ignoreScope === "equipment_number" &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        toggleDropdown(false);
      }
    };

    if (groupDropdownOpen || dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ignoreScope, groupDropdownOpen, dropdownOpen, closeDropdown]);

  useEffect(() => {
    const fetchEquipmentGroups = async () => {
      try {
        const response = await axiosInstance.get(
          `${API_URL}/get_equipment_group`,
          { params: { project_selected: selectedProject } }
        );
        setEquipmentGroups(response.data?.data ?? []);
      } catch (error) {
        console.error("Error fetching equipment groups:", error);
      }
    };
    if (selectedProject && ignoreScope === "equipment_name") {
      fetchEquipmentGroups();
    }
  }, [selectedProject, ignoreScope]);

  // Selection helpers (operate on strings)
  const toggleEquipment = (eqNo) => {
    setSelectedEquipment((prev) =>
      prev.includes(eqNo) ? prev.filter((id) => id !== eqNo) : [...prev, eqNo]
    );
  };

  const toggleAllEquipment = () => {
    if (selectedEquipment.length === equipmentNumbers.length) {
      setSelectedEquipment([]);
    } else {
      setSelectedEquipment(equipmentNumbers);
    }
  };

  // Labels
  const numberToggleLabel =
    selectedEquipment.length > 0
      ? `${selectedEquipment.length} equipment number(s) selected`
      : "Select equipment numbers...";

  const groupToggleLabel = selectedGroup || "Select equipment name...";

  // Custom dropdown toggle component
  const CustomToggle = forwardRef(({ children, onClick, isOpen }, ref) => (
    <button
      ref={ref}
      className={`bulk-warnings-dropdown-toggle ${isOpen ? "show" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      aria-expanded={isOpen}
      type="button"
    >
      {children}
    </button>
  ));

  return (
    <div className="bulk-warnings-form-group">
      <label className="bulk-warnings-form-label">
        {ignoreScope === "equipment_name" ? "Equipment Name" : "Equipment Numbers"}
      </label>

      <div
        className="bulk-warnings-types-dropdown mb-3"
        ref={ignoreScope === "equipment_name" ? groupDropdownRef : dropdownRef}
      >
        <Dropdown
          className="mt-0"
          show={ignoreScope === "equipment_name" ? groupDropdownOpen : dropdownOpen}
          onToggle={toggleDropdown}
          style={{ padding: "0px", border: "unset", boxSahdow: "none", borderRadius: "4px", marginTop: "-5px !important" }}
        >
          <Dropdown.Toggle
            as={CustomToggle}
            isOpen={ignoreScope === "equipment_name" ? groupDropdownOpen : dropdownOpen}
          >
            {ignoreScope === "equipment_name" ? groupToggleLabel : numberToggleLabel}
          </Dropdown.Toggle>

          <Dropdown.Menu
            className={`bulk-warnings-dropdown-menu ${ignoreScope === "equipment_name"
                ? groupDropdownOpen
                  ? "show"
                  : ""
                : dropdownOpen
                  ? "show"
                  : ""
              }`}
          >
            {ignoreScope === "equipment_name" ? (
              <>
                {groupNames.length === 0 && (
                  <div className="bulk-warnings-dropdown-item disabled">
                    No groups found
                  </div>
                )}
                {groupNames.map((group) => (
                  <div
                    key={group}
                    className={`bulk-warnings-dropdown-item ${selectedGroup === group ? "is-selected" : ""
                      }`}
                    onClick={() => {
                      setSelectedGroup(group);
                      toggleDropdown(false);
                    }}
                  >
                    {group}
                  </div>
                ))}
              </>
            ) : (
              <>
                <div
                  className="bulk-warnings-dropdown-item border-bottom"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAllEquipment();
                  }}
                  style={{
                    position: 'sticky',
                    top: "-10px",
                    backgroundColor: '#fff',
                    zIndex: 10,
                    borderBottom: '1px solid #eef1f4',
                    padding: '10px 10px'
                  }}
                >
                  <div className="bulk-warnings-checkbox-wrapper">
                    <input
                      type="checkbox"
                      className="bulk-warnings-checkbox"
                      checked={
                        equipmentNumbers.length > 0 &&
                        selectedEquipment.length === equipmentNumbers.length
                      }
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleAllEquipment();
                      }}
                    />
                    <label className="bulk-warnings-checkbox-label fw-bold">
                      Select All Equipment
                    </label>
                  </div>
                </div>

                {equipmentNumbers.map((eqNo) => (
                  <div
                    key={eqNo}
                    className={`bulk-warnings-dropdown-item ${selectedEquipment.includes(eqNo) ? "is-selected" : ""
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleEquipment(eqNo);
                    }}
                  >
                    <div className="bulk-warnings-checkbox-wrapper">
                      <input
                        type="checkbox"
                        className="bulk-warnings-checkbox"
                        checked={selectedEquipment.includes(eqNo)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleEquipment(eqNo);
                        }}
                      />
                      <label className="bulk-warnings-checkbox-label">{eqNo}</label>
                    </div>
                  </div>
                ))}
                {equipmentNumbers.length === 0 && (
                  <div className="bulk-warnings-dropdown-item disabled">
                    No equipment found
                  </div>
                )}
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};

export default EquipmentMultiSelectDropdown;
